import React, { useEffect } from 'react';

import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableGlobalBrandFilterApplied,
  fetchAllGlobalBrands,
  // fetchGlobalBrandFilters,
  refreshGlobalBrands,
  setGlobalBrandLimit,
  setGlobalBrandPage,
  setGlobalBrandSortBy,
  setGlobalBrandSortField,
  // updateGlobalBrandFilters,
} from '../../../redux/slices/GlobalBrandSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';

/**
 * Global brand management component — displays globally approved brands.
 * @returns {React.JSX.Element} Global brand management component
 */
function Global() {
  const dispatch = useAppDispatch();
  const {
    brands,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    // filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.globalBrand);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllGlobalBrands({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllGlobalBrands({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableGlobalBrandFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setGlobalBrandSortBy(sortData.sortBy));
    dispatch(setGlobalBrandSortField(sortData.sortField));
    dispatch(
      fetchAllGlobalBrands({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setGlobalBrandPage(value.page));
    if (value.limit !== undefined) dispatch(setGlobalBrandLimit(value.limit));
    dispatch(
      fetchAllGlobalBrands({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes global brand data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllGlobalBrands({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes global brand data using current filters for table refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      refreshGlobalBrands({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  const column = [
    {
      label: 'Brand Name',
      accessorKey: 'name',
    },
    {
      label: 'Phone Number',
      accessorKey: 'phoneNumber',
      /**
       * Renders phone number
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>
          {currentRow.phoneNumber
            ? `${currentRow.countryCode || ''} ${currentRow.phoneNumber}`
            : 'N/A'}
        </span>
      ),
    },
  ];

  return (
    <CustomTable
      dataKey="GlobalBrandManagement"
      header={column}
      data={brands}
      serialNo
      version="v2"
      loading={loading}
      page={page}
      limit={limit}
      height={350}
      created
      lastUpdated
      totalPages={totalPages}
      totalResults={totalResults}
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortFunction}
      pageLimitChange={handlePageLimitChange}
      sizeChangeButtonRequired
      // filters={filters}
      // filtersToDispatch={updateGlobalBrandFilters}
      // filterListLoading={filterListLoading}
      // filterListToDisfpatch={fetchGlobalBrandFilters}
      refreshRequired
      handleRefresh={handleRefresh}
      hoverLabel="Refresh"
      refershCurrentPages={handleRefresh}
      onHiddenColumnsChange={() => {}}
      requiredRefreshToggle
      refreshFunction={handleAutoRefresh}
    />
  );
}

export default Global;
